<template>
  <div>
    <Loading :visible="!mapObject" />
    <BreadcrumbMapObject v-if="mapObject"
                         :mapObject="mapObject"
                         :extraLabel="$t('button.split')"
                         @clickBack="clickBack"
                         :show-parents="false"
    />
    <CollapsePanel
      :collapsed="true"
      :loading=loading
      :showCount=false
      :title="$t('route_management.object_information')"
    >
      <div v-if="mapObject" class="object-information-block">
        <div v-if="numberOfLinePoints === 2">
          <p class="not-enough-points">
            {{ $t(`drawing.split_error_not_enough_points`) }}
          </p>
        </div>
        <div v-else>
          <p>
            {{ $t(`drawing.split_information`) }}
          </p>
          <div class="field">
            <label for="map-object-type-id" class="type-label">
              {{ $t('drawing.split_type') }}:
            </label>
            <div class="field content">
              <MultiSelect
                id="map-object-type-id"
                :options="Object.values(availableTypes)"
                v-model="type"
                track-by="id"
                label="name"
                :multiple="false"
                :close-on-select="true"
                :allow-empty="false"
                :preselect-first="true"
                placeholder=""
                selectLabel=""
                selectedLabel=""
                deselectLabel=""
                @input="updateType"
              />
            </div>
            <span class="input-error">
              {{ errors.type }}
            </span>
          </div>
          <div class="field">
            <label for="name">{{ $t('drawing.split_name') }}:</label>
            <input
              id="name"
              name="name"
              type="text"
              v-model="name"
              :class="{'error': errors.name !== null}"
              @change="resetError('name')"
              autofocus
            />
            <span class="input-error">
              {{ errors.name }}
            </span>
          </div>
        </div>
      </div>
    </CollapsePanel>
    <ButtonRow @back="clickBack" @save="splitObject" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MultiSelect from 'vue-multiselect';
import MapObjectApi from '@/api/map_object';
import Loading from '@/components/Loading.vue';
import BreadcrumbMapObject from '@/components/Layout/BreadcrumbMapObject.vue';
import CollapsePanel from '@/components/CollapsePanel.vue';
import ButtonRow from '@/components/ButtonRow.vue';
import EventBus from '@/events/event-bus';
import MapMode from '@/enums/MapMode';
import GeoType from '@/enums/GeoType';

export default {
  name: 'drawing.split_object',
  components: {
    ButtonRow,
    CollapsePanel,
    BreadcrumbMapObject,
    Loading,
    MultiSelect,
  },
  computed: {
    ...mapGetters({
      mapObject: 'selected/getMapObject',
      mapObjectTypes: 'settings/getMapObjectTypes',
      network: 'settings/getNetworkModel',
    }),
    availableTypes() {
      if (this.mapObjectTypes === null || this.mapObjectTypes === undefined) {
        return [];
      }
      if (Object.values(this.mapObjectTypes).length === 0) {
        return [];
      }
      return Object.values(this.mapObjectTypes)
        .filter(item => item.geo_type === GeoType.point)
        .filter(item => item.on_network);
    },
    numberOfLinePoints() {
      console.log('numberOfLinePoints', this.mapObject);
      if (this.mapObject === null || this.mapObject === undefined) {
        return null;
      }
      const coordinates = this.getCoordinates(this.mapObject, 'linestring');
      if (coordinates === null || coordinates.length < 1) {
        return null;
      }
      return coordinates.length;
    },
  },
  data() {
    return {
      loading: false,
      chosenIndex: null,
      type: null,
      name: '',
      errors: {
        name: null,
        type: null,
      },
    };
  },
  methods: {
    ...mapActions({
      fetchMapObject: 'selected/fetchMapObject',
      resetMapObject: 'selected/resetMapObject',
      addMapObject: 'settings/addMapObject',
      updateMapObject: 'settings/updateMapObject',
      getChildrenForMapObject: 'settings/getChildrenForMapObject',
    }),
    clickBack() {
      EventBus.$emit('Map.changeMapMode', MapMode.objectSelect);
      this.$router.push({ name: 'drawing.show', params: { id: this.mapObject.id } });
    },
    async splitObject() {
      if (this.numberOfLinePoints === null || this.numberOfLinePoints === 2) {
        return;
      }
      if (!this.validateFields()) {
        return;
      }
      if (!this.chosenIndex) {
        this.$notify({
          type: 'error',
          title: this.$t('drawing.split_error_no_index_chosen'),
        });
        return;
      }
      this.loading = true;

      const data = {
        data: {
          coordinateIndex: this.chosenIndex,
          name: this.name,
          map_object_type_id: this.type.id,
        },
      };

      const children = await this.getChildrenForMapObject({ id: this.mapObject.id });

      const coordinates = this.getCoordinates(this.mapObject, 'linestring');

      const closestIndexes = [];

      // Check closest coordinate for each child to determine if it needs to be under which splitted line
      children.forEach((child) => {
        let closestIndex = null;
        let minDistance = 999999999;
        coordinates.forEach((coordinate, index) => {
          const childCoordinates = this.getCoordinates(child, 'point');
          const distanceToChild = this.getDistance(childCoordinates, coordinate);
          if (distanceToChild < minDistance) {
            minDistance = distanceToChild;
            closestIndex = index;
          }
        });
        if (closestIndex !== null) {
          closestIndexes.push({
            closestIndex,
            child,
          });
        }
      });


      MapObjectApi.splitMapObject(this.mapObject.id, data)
        .then(async (response) => {
          response.data.forEach((object) => {
            if (object.id !== this.mapObject.id) {
              this.addMapObject({ mapObject: object });
            }
          });

          // Check and update children that need to move to a different parent line
          closestIndexes.forEach((item) => {
            if (item.closestIndex > this.chosenIndex) {
              const child = JSON.parse(JSON.stringify(item.child));
              child.parent_id = response.data[2].id;
              this.updateMapObject({ mapObject: child });
            }
          });
          await this.fetchMapObject({ id: this.mapObject.id, force: true });
          this.loading = false;
          this.$notify({
            type: 'success',
            title: this.$t('dialog.created', {
              model: this.$options.filters.capitalize(this.$tc('models.mapobject', 1)),
              name: this.name,
            }),
          });
          EventBus.$emit('Map.redraw');
          EventBus.$emit('Map.changeMapMode', MapMode.objectSelect);
          return this.$router.push({ name: 'drawing.show', params: { id: this.mapObject.id } });
        })
        .catch((error) => {
          this.$notify({
            type: 'error',
            title: this.$t('error.create_failed', { model: this.$tc('models.mapobject', 1) }),
            data: error.response,
          });
          this.loading = false;
          return false;
        });
    },
    resetError(field) {
      this.errors[field] = null;
    },
    validateFields() {
      let valid = true;
      if (this.name === '') {
        this.errors.name = this.$t('validation.name_empty');
        this.$notify({
          type: 'warning',
          title: this.$t('error.create_failed', { model: this.$tc('models.mapobject', 1) }),
          text: this.$t('validation.name_empty'),
          duration: 1000,
        });
        valid = false;
      }
      if (this.type === null) {
        this.errors.name = this.$t('validation.field_empty', { field: this.$tc('models.mapobjecttype', 1) });
        this.$notify({
          type: 'warning',
          title: this.$t('error.create_failed', { model: this.$tc('models.mapobject', 1) }),
          text: this.$t('validation.field_empty', { field: this.$tc('models.mapobjecttype', 1) }),
          duration: 1000,
        });
        valid = false;
      }
      return valid;
    },
    fetchNameForNewObject(typeId) {
      MapObjectApi.getNameForNewObject(typeId).then((response) => {
        if (response && response.data && response.data.name) {
          this.name = response.data.name;
        }
      });
    },
    updateType(value) {
      if (!value) {
        this.type = null;
      } else {
        this.type = value;
        if (value.id) {
          this.fetchNameForNewObject(value.id);
        }
      }
    },
  },
  mounted() {
    if (this.$route.params.id !== null && this.$route.params.id !== undefined) {
      const id = parseInt(this.$route.params.id, 10);
      if (!this.mapObject || this.mapObject.id !== id) {
        this.fetchMapObject({ id });
      }
    }
    EventBus.$emit(
      'Map.changeMapMode',
      MapMode.splitLine,
    );

    EventBus.$on('Map.shareSplitLineIndex', (index) => {
      this.chosenIndex = index;
    });
  },
};
</script>

<style scoped>
.object-information-block {
  padding: 0 20px 40px 20px;
  overflow: inherit;
}
.field {
  overflow: visible;
  min-height: 40px;
}
.field label {
  width: auto;
}
.type-label {
  display: inline-flex;
  float: initial;
}
.input-error {
  float: left;
  width: 100%;
  color: #ff7676;
}
input.error {
  border-color: #ff7676;
}
p.not-enough-points {
  font-style: italic;
}
</style>
